/**
 * 保存していない変更があるページから離れるときの確認(2026-09-10 指示)。
 *  - 画面内の遷移(メニュー・リンク・ブラウザの戻る・月の切替): useBlocker で止めて確認モーダルを出す
 *  - タブを閉じる・再読み込み: beforeunload でブラウザ標準の確認
 * 使い方: const guard = useUnsavedGuard(dirty); … {guard.modal}
 * (createBrowserRouter(データルーター)が前提。App.tsx)
 */
import { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';
import Modal from './Modal';

const DEFAULT_MESSAGE = '保存されていないデータがあります。このまま移動すると入力した内容は失われます。';

export function useUnsavedGuard(dirty: boolean, message = DEFAULT_MESSAGE): { modal: React.ReactNode; blocked: boolean } {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => dirty && (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search),
  );

  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // 古いブラウザ向け(表示文言はブラウザ既定)
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // 保存などで変更が無くなったら、止めていた遷移の状態を戻す
  useEffect(() => {
    if (blocker.state === 'blocked' && !dirty) blocker.reset();
  }, [blocker, dirty]);

  const modal =
    blocker.state === 'blocked' ? (
      <Modal onClose={() => blocker.reset()} width="min(480px, 94vw)" showClose={false}>
        <h3>保存されていないデータがあります</h3>
        <p style={{ margin: '8px 0 0' }}>{message}</p>
        <div className="modal-actions center">
          <button className="btn-green" onClick={() => blocker.reset()}>
            この画面にとどまる
          </button>
          <button className="btn-line danger" onClick={() => blocker.proceed()}>
            保存せずに移動する
          </button>
        </div>
      </Modal>
    ) : null;

  return { modal, blocked: blocker.state === 'blocked' };
}
